import { ListDateFilters, ListPagination } from "./NotificationListControls";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuthContext } from "../../../Components/AuthContext";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Alert, Button, Card, CardBody, Col, Container, Input, Row, Spinner, Table } from "../../../Components/ui";
import {
  AlimTalkAccountMode,
  NotificationCategory,
  NotificationChannel,
  NotificationHistoryItem,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import { hasCapability } from "../../../common/permissions";
import AlimTalkChannelSettings from "./AlimTalkChannelSettings";
import AlimTalkTemplateManagement from "./AlimTalkTemplateManagement";
import HqAdminSettings from "./HqAdminSettings";
import { SITE_ID } from "../../../assets/meta/meta";
import NotificationSendComposer from "./NotificationSendComposer";
import SystemMessageSettings from "./SystemMessageSettings";
import {
  getAllowedNotificationCenterPages,
  getNotificationCenterFallbackPage,
  getNotificationCenterPage,
  getNotificationCenterPageIdFromPath,
  isNotificationCenterPageId,
} from "./notificationCenterAccess";
import { normalizeAlimTalkAccountMode } from "./alimtalkTemplate";

const categoryMeta: Array<{ value: NotificationCategory; label: string }> = [
  { value: "payment", label: "결제 관련" },
  { value: "usage", label: "이용관련" },
  { value: "site_notice", label: "지점 공지" },
  { value: "marketing", label: "마케팅" },
  { value: "mandatory", label: "필수 고지" },
];

const channelLabel: Record<NotificationChannel, string> = {
  web_push: "Web Push",
  app_push: "App Push",
  alimtalk: "알림톡",
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("ko-KR");
};

export default function NotificationCenter() {
  const { userInfo, isLoading: authLoading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedPage = getNotificationCenterPageIdFromPath(location.pathname);
  const legacyTab = new URLSearchParams(location.search).get("tab");
  const [accountMode, setAccountMode] = useState<AlimTalkAccountMode | null>(null);
  const [accountModeResolved, setAccountModeResolved] = useState(false);
  const [history, setHistory] = useState<NotificationHistoryItem[]>([]);
  const historyGeneration = useRef(0);
  const [historyQuery, setHistoryQuery] = useState({ page: 1, channel: "", state: "", dispatch_state: "", created_from: "", created_to: "" });
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const notificationId = new URLSearchParams(location.search).get("notification_id") || undefined;
  const [historyCount, setHistoryCount] = useState(0);
  const [historyCategory, setHistoryCategory] = useState<NotificationCategory | "">("");
  const [historySearch, setHistorySearch] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [webPushConfigured, setWebPushConfigured] = useState<boolean | null>(null);
  const needsAccountMode = Boolean(
    !userInfo?.is_global_admin && hasCapability(userInfo, "site_owner"),
  );

  useEffect(() => {
    let active = true;
    if (!needsAccountMode) {
      setAccountMode(null);
      setAccountModeResolved(true);
      return () => { active = false; };
    }
    setAccountModeResolved(false);
    void notificationCenterService.getAlimTalkAccount()
      .then((response) => {
        if (active) setAccountMode(normalizeAlimTalkAccountMode(response.data.account_mode, response.data.using_default));
      })
      .catch(() => {
        if (active) setAccountMode(null);
      })
      .finally(() => {
        if (active) setAccountModeResolved(true);
      });
    return () => { active = false; };
  }, [needsAccountMode]);

  const accessLoading = authLoading || (needsAccountMode && !accountModeResolved);
  const allowedPages = useMemo(() => (
    getAllowedNotificationCenterPages(userInfo, accountMode)
  ), [accountMode, userInfo]);
  const fallbackPage = getNotificationCenterFallbackPage(allowedPages);
  const activePage = accessLoading
    ? null
    : requestedPage && allowedPages.includes(requestedPage)
      ? requestedPage
      : isNotificationCenterPageId(legacyTab) && allowedPages.includes(legacyTab)
        ? legacyTab
        : fallbackPage;
  const activePageItem = activePage ? getNotificationCenterPage(activePage) : null;

  useEffect(() => {
    if (accessLoading || !activePageItem) return;
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete("tab");
    const nextSearch = nextSearchParams.toString();
    if (location.pathname === activePageItem.path && !legacyTab) return;
    navigate(
      {
        pathname: activePageItem.path,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [accessLoading, activePageItem, legacyTab, location.pathname, location.search, navigate]);

  const loadHistory = useCallback(async () => {
    const generation = ++historyGeneration.current;
    setLoadingHistory(true);
    setHistory([]);
    try {
      const response = await notificationCenterService.getHistory({
        ...historyQuery, page_size: 20, notification_id: notificationId,
        category: historyCategory || undefined,
        search: historySearch || undefined,
      });
      if (generation !== historyGeneration.current) return;
      setHistory(Array.isArray(response.data.results) ? response.data.results : []);
      setHistoryCount(Number(response.data.count || 0));
    } catch (error: any) {
      if (generation === historyGeneration.current) { setHistoryCount(0); toast.error(error?.response?.data?.detail || "알림 내역을 불러오지 못했습니다."); }
    } finally {
      if (generation === historyGeneration.current) setLoadingHistory(false);
    }
  }, [historyCategory, historySearch, historyQuery, notificationId]);

  const loadWebPushConfig = useCallback(async () => {
    try {
      const response = await notificationCenterService.getWebPushConfig();
      setWebPushConfigured(Boolean(response.data.is_configured));
    } catch {
      setWebPushConfigured(false);
    }
  }, []);

  useEffect(() => {
    if (activePage === "history") void loadHistory();
    return () => { historyGeneration.current += 1; };
  }, [activePage, loadHistory]);

  useEffect(() => {
    if (activePage === "channels" && webPushConfigured === null) void loadWebPushConfig();
  }, [activePage, loadWebPushConfig, webPushConfigured]);

  const categoryLabel = useMemo(() => (
    Object.fromEntries(categoryMeta.map((item) => [item.value, item.label]))
  ), []);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={activePageItem?.label || "알림 센터"} pageTitle="알림&메시지" />

        <Card>
          <CardBody>
            {accessLoading ? <div className="text-center py-4"><Spinner size="sm" className="me-2" />접근 권한을 확인하고 있습니다.</div> : null}
            {!accessLoading && !activePage ? <Alert color="warning">알림 센터를 사용할 권한이 없습니다.</Alert> : null}
            {activePage === "send" ? <NotificationSendComposer key={`${SITE_ID}:${userInfo?.id}`} /> : null}

            {activePage === "history" ? (
              <>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Input type="select" value={historyCategory} aria-label="알림 분류" onChange={(event) => { setHistoryCategory(event.target.value as NotificationCategory | ""); setHistoryQuery((q) => ({ ...q, page: 1 })); }} style={{ maxWidth: 180 }}>
                    <option value="">전체 분류</option>
                    {categoryMeta.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </Input>
                  <Input value={historySearch} onChange={(event) => { setHistorySearch(event.target.value); setHistoryQuery((q) => ({ ...q, page: 1 })); }} onKeyDown={(event) => { if (event.key === "Enter") void loadHistory(); }} placeholder="고객명, 전화번호, 제목 검색" style={{ maxWidth: 280 }} />
                  <Button color="soft-primary" onClick={() => void loadHistory()} disabled={loadingHistory}>조회</Button>
                  <Input aria-label="발송 채널" type="select" value={historyQuery.channel} onChange={(event) => setHistoryQuery({ ...historyQuery, page: 1, channel: event.target.value })} style={{ maxWidth: 150 }}><option value="">전체 채널</option>{Object.entries(channelLabel).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</Input>
                  <Input aria-label="발송 결과" type="select" value={historyQuery.state} onChange={(event) => setHistoryQuery({ ...historyQuery, page: 1, state: event.target.value })} style={{ maxWidth: 150 }}><option value="">전체 결과</option><option value="accepted">접수</option><option value="delivered">최종 성공</option><option value="failed">실패</option><option value="processing">확인 중</option></Input>
                  <Input aria-label="발송 작업" type="select" value={historyQuery.dispatch_state} onChange={(event) => setHistoryQuery({ ...historyQuery, page: 1, dispatch_state: event.target.value })} style={{ maxWidth: 150 }}><option value="">전체 작업</option><option value="pending">발송 대기</option><option value="processing">처리 중</option><option value="review">확인 필요</option></Input>
                  <ListDateFilters from={historyQuery.created_from} to={historyQuery.created_to} onChange={(created_from, created_to) => setHistoryQuery({ ...historyQuery, page: 1, created_from, created_to })} />
                  {notificationId ? <Button color="light" onClick={() => navigate("/notification-center/history")}>발송 #{notificationId} 필터 해제</Button> : null}
                </div>
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead><tr><th>등록 시각</th><th>수신자</th><th>분류</th><th>제목</th><th>채널 결과</th><th>알림함 읽음</th></tr></thead>
                    <tbody>
                      {loadingHistory ? <tr><td colSpan={6} className="text-center py-4"><Spinner size="sm" /></td></tr> : null}
                      {!loadingHistory && history.length === 0 ? <tr><td colSpan={6} className="text-center py-4 text-muted">발송 이력이 없습니다.</td></tr> : null}
                      {history.map((item) => (
                        <tr key={item.id}>
                          <td className="text-nowrap">{formatDateTime(item.created_at)}</td>
                          <td>{item.user_name || "-"}<br /><small className="text-muted">{item.phone_num || ""}</small></td>
                          <td>{categoryLabel[item.category] || item.category}</td>
                          <td><strong>{item.title}</strong><small className="text-muted"> · #{item.id}</small><br /><small className="text-muted text-break">{item.body}</small></td>
                          <td>
                            {item.dispatch_state && ["pending", "processing", "review"].includes(item.dispatch_state) ? <div className="text-warning">{{ pending: "발송 대기", processing: "발송 처리 중", review: "확인 필요 · 중복 발송 방지를 위해 보류" }[item.dispatch_state]}</div> : null}
                            {item.deliveries?.length ? item.deliveries.map((delivery) => <div key={delivery.id} className="mb-2">
                              {channelLabel[delivery.channel] || delivery.channel} · {({ accepted: "접수", delivered: "최종 성공", failed: "실패", processing: "확인 중", pending: "대기", skipped: "건너뜀", cancelled: "취소" } as Record<string, string>)[delivery.state] || delivery.state}
                              {delivery.error_message ? <div className="text-danger small">{delivery.error_message}</div> : null}
                              {delivery.result_detail?.state === "review" ? <div className="text-warning small">결과 확인 필요</div> : null}
                              {delivery.provider_message_id ? <div className="small text-muted text-break">발송 ID: {delivery.provider_message_id}</div> : null}
                              {delivery.result_detail?.completed_at ? <div className="small text-muted">최종 결과: {formatDateTime(delivery.result_detail.completed_at)}</div> : null}
                              {delivery.result_detail?.checked_at ? <div className="small text-muted">결과 확인: {formatDateTime(delivery.result_detail.checked_at)}{delivery.result_detail.code ? ` (${delivery.result_detail.code})` : ""}</div> : null}
                            </div>) : "알림함"}
                            {hasCapability(userInfo, "site_manager") && item.deliveries?.some((delivery) => delivery.channel === "alimtalk" && delivery.provider_message_id) ? <Button size="sm" color="light" disabled={syncingId !== null} onClick={async () => {
                              setSyncingId(item.id);
                              try { await notificationCenterService.syncAlimTalkResult(item.id); await loadHistory(); }
                              catch (error: any) { toast.error(error?.response?.data?.detail || "발송 결과를 확인하지 못했습니다."); }
                              finally { setSyncingId(null); }
                            }}>{syncingId === item.id ? "확인 중" : "알림톡 결과 확인"}</Button> : null}
                          </td>
                          <td>{item.read_at ? formatDateTime(item.read_at) : "미확인"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <ListPagination label="발송 내역" page={historyQuery.page} count={historyCount} loading={loadingHistory} onChange={(page) => setHistoryQuery({ ...historyQuery, page })} />
              </>
            ) : null}

            {activePage === "system" ? <SystemMessageSettings /> : null}
            {activePage === "templates" ? <AlimTalkTemplateManagement /> : null}
            {activePage === "hq" ? <HqAdminSettings /> : null}

            {activePage === "channels" ? (
              <>
                <Row className="g-3 mb-3">
                  <Col lg={4}><div className="border rounded p-3 h-100"><h5>Web Push</h5><p className="mb-0">{webPushConfigured === null ? "상태 확인 중" : webPushConfigured ? "사용 가능" : "VAPID 설정 필요"}</p></div></Col>
                  <Col lg={4}><div className="border rounded p-3 h-100"><h5>App Push</h5><p className="mb-0">Expo 토큰 기반 발송</p></div></Col>
                  <Col lg={4}><div className="border rounded p-3 h-100"><h5>알림톡</h5><p className="mb-0">계정 방식, 연결 상태와 충전금을 아래에서 확인합니다.</p></div></Col>
                </Row>
                <AlimTalkChannelSettings />
              </>
            ) : null}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
